import React, {ReactNode, useEffect, useRef, useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SignaturePad from 'signature_pad';
import {WorkItemType} from '../../../shared/dtos/estimate-tree.dto';
import {EstimateCostItemDto} from '../../../shared/dtos/estimate-cost-item.dto';
import {ClientContactDto} from '../../client/dtos/client-contact.dto';
import {AddEntryBox, EntryEditor} from '../../client/profile/ClientCard';
import {ContactEntry, ContactKind, KIND_LABEL, phoneDigits} from '../../client/profile/contact-entries';
import treeIcon from '../../../assets/tree_icon.png';
import treeGroupIcon from '../../../assets/tree_group_icon.png';
import fallenTreeIcon from '../../../assets/fallen_tree_icon.png';
import stumpIcon from '../../../assets/stump_icon.png';
import debrisPileIcon from '../../../assets/debris_pile_icon.png';
import miscIcon from '../../../assets/miscellaneous_icon.png';
import {cleanCostItemPrice, formatCostItemPrice, money, parseCostItemPrice} from './record-math';
import {ADD_MENU_LABEL} from './operation-schema';
import {useTimedNotice} from './timed-notice';
import {GateGlyph} from './map/map-art';

/*
 * Everything below the work items (EstimateFooterView.kt, WorkItemsView.kt
 * AddWorkItemRow): the add menu and the cost items in edit mode, the Job
 * Total card, and the footer for the estimate's status, with the send block
 * and the customer's signature. Full-width buttons stop at about 420 and sit
 * centred on a wide page (SQ-596); Android's dialogs stay small.
 */

// ---- Add a work item ------------------------------------------------------------------------

const ADD_ICONS: Record<string, string> = {
  Tree: treeIcon, TreeGroup: treeGroupIcon, FallenTree: fallenTreeIcon, Stump: stumpIcon, DebrisPile: debrisPileIcon, Misc: miscIcon,
};

/** AddWorkItemRow: a circle per kind in a gray tray; Gate puts a gate on the map. */
export const AddWorkItemRow = ({enabled, onAdd, onAddGate}: {enabled: boolean; onAdd: (type: WorkItemType) => void; onAddGate: () => void}) => (
  <div className="we-tray we-add-items">
    <div className="we-tray-head we-tray-head--static"><span>Add a work item</span></div>
    <div className="we-add-grid">
      {Object.values(WorkItemType).map(type => (
        <button type="button" key={type} className="we-add-kind" disabled={!enabled} onClick={() => onAdd(type)} aria-label={`Add ${ADD_MENU_LABEL[type]}`}>
          <span className="we-add-circle"><img src={ADD_ICONS[type]} alt="" width={22} height={22}/></span>
          <span className="we-add-label">{ADD_MENU_LABEL[type]}</span>
        </button>
      ))}
      <button type="button" className="we-add-kind" disabled={!enabled} onClick={onAddGate} aria-label="Add Gate">
        {/* The map's own gate drawing, shrunk into the bubble, as Android's (GateGlyph at 27 wide). */}
        <span className="we-add-circle"><GateGlyph width={27}/></span>
        <span className="we-add-label">Gate</span>
      </button>
    </div>
  </div>
);

// ---- Cost items ----------------------------------------------------------------------------

/**
 * A cost item's price as typed: what was typed stays for as long as it means
 * the saved price ("50." is not turned back into "50"), the formatted price
 * otherwise.
 */
const CostItemPrice = ({price, onChange}: {price: number; onChange: (p: number) => void}) => {
  const [typed, setTyped] = useState<string | null>(null);
  const same = typed != null && Math.round(parseCostItemPrice(typed) * 100) === Math.round(price * 100);
  const shown = same ? typed! : price === 0 ? '' : formatCostItemPrice(price);
  return (
    <label className="we-select we-cost-price">
      <span className="we-select-title">$</span>
      <input inputMode="decimal" value={shown} placeholder="0.00" onChange={e => {
        const clean = cleanCostItemPrice(e.target.value);
        setTyped(clean);
        onChange(parseCostItemPrice(clean));
      }}/>
    </label>
  );
};

export const CostItemsEdit = ({items, onChange, onAdd, onDelete}: {
  items: EstimateCostItemDto[];
  onChange: (id: number, patch: Partial<EstimateCostItemDto>) => void;
  onAdd: () => void;
  onDelete: (id: number) => void;
}) => (
  <div className="we-costs">
    {items.map(item => (
      <div key={item.id} className="we-cost">
        <label className="we-select">
          <span className="we-select-title">Description</span>
          <input value={item.description} placeholder="Description" autoCapitalize="sentences" onChange={e => onChange(item.id, {description: e.target.value})}/>
        </label>
        <CostItemPrice price={item.price} onChange={price => onChange(item.id, {price})}/>
        <button type="button" className="we-trash" onClick={() => onDelete(item.id)} aria-label="Remove"><DeleteIcon/></button>
      </div>
    ))}
    <button type="button" className="we-tonal-wide" onClick={onAdd}>+ Add Additional Cost Item (dump fee, etc.)</button>
  </div>
);

// ---- Job Total -------------------------------------------------------------------------------

/** One line of the Job Total card; `amount` (dollars) rides on it as data-amount, so a typed total still reads. */
export const TotalRow = ({label, value, amount, time, tag, strong}: {
  label: string; value: ReactNode; amount?: number; time?: string; tag?: ReactNode; strong?: boolean;
}) => (
  <div className={strong ? 'er-total-row er-total-row--job' : 'er-total-row'} role="group" aria-label={label}
       data-amount={amount === undefined ? undefined : amount.toFixed(2)}>
    <span>{label}</span>
    {tag}
    {time && <span className="er-total-time">{time}</span>}
    <span className="er-total-value">{value}</span>
  </div>
);

/**
 * The Job Total as an editable price (JobTotalField.kt): the dollars are
 * typed, the cents follow in grey. Leaving the field (or Enter) spreads the
 * difference over labor; equipment and cost items never move, so a total
 * below them is refused.
 */
/**
 * SQ-660: while `blockedReason` says an operation has no price, the box is
 * faded and a tap shows the reason under it for a few seconds. A total that
 * would take an operation to $0 (`laborRefusal`) is not applied, and says so.
 */
export const EditableJobTotal = ({total, fixed, onSetLaborTotal, blockedReason, laborRefusal}: {
  total: number; fixed: number; onSetLaborTotal: (labor: number) => void;
  blockedReason?: string; laborRefusal?: (labor: number) => string | undefined;
}) => {
  const cents = Math.round(total * 100);
  const saved = String(Math.trunc(cents / 100));
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useTimedNotice();
  // A reason that no longer holds goes at once: the operations were priced, or the total moved.
  useEffect(() => setNotice(undefined), [blockedReason, total, setNotice]);
  const commit = () => {
    const text = draft;
    setDraft(null);
    if (text == null || text === saved) return;
    const digits = text.split('.')[0].replace(/\D/g, '');
    if (!digits) { setError(undefined); return; }
    const fixedCents = Math.round(fixed * 100);
    const totalCents = Number(digits) * 100 + fixedCents % 100;
    if (totalCents < fixedCents) {
      setError(`The job total can't be less than equipment and additional cost items (${money(fixedCents / 100)}).`);
      return;
    }
    setError(undefined);
    const labor = (totalCents - fixedCents) / 100;
    const refusal = laborRefusal?.(labor);
    if (refusal) setNotice(refusal); else onSetLaborTotal(labor);
  };
  const shown = (draft ?? saved).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (
    <span className="er-job-total-edit">
      <span className={blockedReason ? 'er-job-total-field er-job-total-field--off' : 'er-job-total-field'}
            onClick={blockedReason ? () => setNotice(blockedReason) : undefined}>
        <span>$</span>
        <input inputMode="numeric" aria-label="Job Total" value={shown} size={Math.max(1, shown.length)} readOnly={!!blockedReason}
               aria-disabled={!!blockedReason}
               onChange={e => setDraft(e.target.value.replace(/\D/g, ''))}
               onBlur={commit} onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}/>
        <span className="er-muted">.{String(Math.abs(cents % 100)).padStart(2, '0')}</span>
      </span>
      {notice && <span className="er-small er-job-total-notice" role="status">{notice}</span>}
      {error && <span className="er-small er-error er-job-total-error">{error}</span>}
    </span>
  );
};

// ---- Footer pieces -----------------------------------------------------------------------------

export const FooterCard = ({children}: {children: ReactNode}) => <div className="er-footer-card">{children}</div>;

export const ComparablesCard = ({onGenerate, disabled}: {onGenerate: () => void; disabled?: boolean}) => (
  <FooterCard>
    <div className="er-footer-note">View work from past jobs your crews completed.</div>
    <button type="button" className="we-ai-button" disabled={disabled} onClick={onGenerate}>Show Comparables &amp; Generate AI Price</button>
  </FooterCard>
);

export const SimpleDialog = ({title, text, confirm, cancel = 'Cancel', danger, onConfirm, onClose}: {
  title: string; text: string; confirm: string; cancel?: string; danger?: boolean; onConfirm?: () => void; onClose: () => void;
}) => (
  <Dialog open onClose={onClose} maxWidth="xs">
    <DialogTitle>{title}</DialogTitle>
    <DialogContent><DialogContentText>{text}</DialogContentText></DialogContent>
    <DialogActions>
      {onConfirm && <Button onClick={onClose}>{cancel}</Button>}
      <Button variant="contained" color={danger ? 'error' : 'primary'} onClick={() => { onClose(); onConfirm?.(); }}>{confirm}</Button>
    </DialogActions>
  </Dialog>
);

// ---- Send (SendRecipientsBlock) ----------------------------------------------------------------------

const digitsOf = (s: string) => s.replace(/\D/g, '');
const READOUT_HINT_MS = 4000;
const EMAIL_HINT = 'Select an email to send to.';
const SMS_HINT = 'Select a phone number to send to.';

interface Extra {key: string; kind: 'phone' | 'email'; value: string; title: string}

/** The client's other phones and emails (Additional Client Info), less any already on the primary lines. */
const extrasOf = (contacts: ClientContactDto[], phone: string, email: string): Extra[] => contacts
  .flatMap(c => [
    c.phoneNumber?.trim() ? {key: `p${c.id}`, kind: 'phone' as const, value: c.phoneNumber.trim(), title: c.title?.trim() ?? ''} : undefined,
    c.email?.trim() ? {key: `e${c.id}`, kind: 'email' as const, value: c.email.trim(), title: c.title?.trim() ?? ''} : undefined,
  ])
  .filter((e): e is Extra => !!e)
  .filter(e => e.kind === 'phone' ? digitsOf(e.value) !== digitsOf(phone) : e.value.toLowerCase() !== email.trim().toLowerCase());

const BLANK_ENTRY = {rowId: null, value: '', city: '', state: '', postalCode: '', title: ''};

/**
 * The client profile's Add box, phones and emails only, and the entry it
 * opens: the same field and "Add a title" link the profile uses (Android's
 * NewRecipientEditor). Add saves it on the client's Additional Client Info
 * and it joins the list above, checked; a failed save keeps what was typed.
 */
const NewRecipientEditor = ({onAdd}: {onAdd: (entry: ContactEntry) => Promise<boolean>}) => {
  const [entry, setEntry] = useState<ContactEntry>();
  const [titling, setTitling] = useState(false);
  const [saving, setSaving] = useState(false);
  if (!entry) {
    return <AddEntryBox kinds={['phone', 'email']} className="cc-add--send"
                        onAdd={(kind: ContactKind) => { setEntry({...BLANK_ENTRY, key: 'send-new', kind}); setTitling(false); }}/>;
  }
  const valid = entry.kind === 'phone' ? phoneDigits(entry.value).length >= 10
    : entry.value.trim().includes('@') && entry.value.trim().includes('.');
  const add = async () => {
    setSaving(true);
    const saved = await onAdd(entry);
    setSaving(false);
    if (saved) setEntry(undefined);
  };
  return (
    <div className="er-send-new">
      <EntryEditor entry={entry} titling={titling} onStartTitling={() => setTitling(true)} onStopTitling={() => setTitling(false)}
                   onChanged={update => setEntry(e => e && update(e))} onTitleChanged={title => setEntry(e => e && {...e, title})}
                   onPicked={() => undefined}/>
      <div className="er-send-new-buttons">
        <Button onClick={() => setEntry(undefined)}>Cancel</Button>
        <button type="button" className="er-tonal er-tonal--compact" disabled={!valid || saving} onClick={() => void add()}>
          Add {KIND_LABEL[entry.kind]}
        </button>
      </div>
    </div>
  );
};

/**
 * Who it goes to and how: the primary phone and email (checked), More
 * Recipients with the client's other phones and emails (unchecked), and
 * Email / SMS as a readout of what the checked lines add up to, not controls.
 * With `onAddRecipient`, More Recipients ends in an Add box (SQ-745, as Android).
 */
export const SendBlock = ({clientName, phone, email, contacts, sending, label, sentTick, onSend, actions, onAddRecipient}: {
  clientName: string; phone: string; email: string; contacts: ClientContactDto[]; sending: boolean; label: string; sentTick: number;
  onSend: (emails: string[], phones: string[]) => void;
  /** The invoice's Send Invoice and Send Receipt (SQ-598), in place of the one send button. */
  actions?: (emails: string[], phones: string[]) => ReactNode;
  /** Saves a new phone or email on the client; the row it made, or undefined when it failed. */
  onAddRecipient?: (entry: ContactEntry) => Promise<ClientContactDto | undefined>;
}) => {
  const [primaryOff, setPrimaryOff] = useState<Set<string>>(new Set());
  const [extrasOn, setExtrasOn] = useState<Set<string>>(new Set());
  const [moreOpen, setMoreOpen] = useState(false);
  const [hint, setHint] = useState<string>();
  const hintTimer = useRef<number>();
  useEffect(() => { setMoreOpen(false); }, [sentTick]);
  useEffect(() => () => window.clearTimeout(hintTimer.current), []);
  const toggle = (set: Set<string>, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  };
  const extras = extrasOf(contacts, phone, email);
  const emails = [...(email.trim() && !primaryOff.has('email') ? [email.trim()] : []),
    ...extras.filter(e => e.kind === 'email' && extrasOn.has(e.key)).map(e => e.value)]
    .filter((v, i, all) => all.findIndex(o => o.toLowerCase() === v.toLowerCase()) === i);
  const phones = [...(phone.trim() && !primaryOff.has('phone') ? [phone.trim()] : []),
    ...extras.filter(e => e.kind === 'phone' && extrasOn.has(e.key)).map(e => e.value)]
    .filter((v, i, all) => all.findIndex(o => digitsOf(o) === digitsOf(v)) === i);
  useEffect(() => {
    if ((hint === EMAIL_HINT && emails.length) || (hint === SMS_HINT && phones.length)) setHint(undefined);
  }, [hint, emails.length, phones.length]);
  const showHint = (text: string) => {
    setHint(text);
    window.clearTimeout(hintTimer.current);
    hintTimer.current = window.setTimeout(() => setHint(undefined), READOUT_HINT_MS);
  };
  const checkedExtras = extras.filter(e => extrasOn.has(e.key)).length;
  const Way = ({name, on, text}: {name: string; on: boolean; text: string}) => (
    <span role="status" className={on ? 'er-send-way er-send-way--on' : 'er-send-way'} onClick={() => !on && showHint(text)}>
      {on ? '✓' : '–'} {name}
    </span>
  );
  return (
    <div className="er-send">
      <div className="er-send-lead">OR send to the customer</div>
      <div className="er-send-caption">PRIMARY</div>
      <div className="er-send-name">{clientName.trim() || 'Client'}</div>
      {phone.trim() && (
        <label className="er-check"><input type="checkbox" checked={!primaryOff.has('phone')} onChange={() => setPrimaryOff(s => toggle(s, 'phone'))}/>{phone}</label>
      )}
      {email.trim() && (
        <label className="er-check"><input type="checkbox" checked={!primaryOff.has('email')} onChange={() => setPrimaryOff(s => toggle(s, 'email'))}/>{email}</label>
      )}
      <div className="er-send-rule"/>
      <button type="button" className="er-send-more" onClick={() => setMoreOpen(o => !o)} aria-expanded={moreOpen}>
        <span>More Recipients</span>
        <span className="er-send-more-end">
          {!moreOpen && checkedExtras > 0 && <span className="er-send-selected">{checkedExtras} selected</span>}
          {moreOpen ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
        </span>
      </button>
      {moreOpen && (
        <div className="er-send-extras">
          {extras.length === 0 && !onAddRecipient && <div className="er-small er-muted">No other phones or emails on this client. Add them on the client's profile.</div>}
          {extras.map(e => (
            <label key={e.key} className="er-check">
              <input type="checkbox" checked={extrasOn.has(e.key)} onChange={() => setExtrasOn(s => toggle(s, e.key))}/>
              <span>{e.title && <span className="er-send-extra-title">{e.title}</span>}{e.value}</span>
            </label>
          ))}
          {onAddRecipient && (
            <NewRecipientEditor onAdd={async entry => {
              const row = await onAddRecipient(entry);
              // Added for this send: it arrives checked.
              if (row) setExtrasOn(s => new Set(s).add(`${entry.kind === 'phone' ? 'p' : 'e'}${row.id}`));
              return !!row;
            }}/>
          )}
        </div>
      )}
      <div className="er-send-rule"/>
      <div className="er-send-by">
        <span className="er-send-lead er-send-lead--by">Sending by</span>
        <Way name="Email" on={emails.length > 0} text={EMAIL_HINT}/>
        <Way name="SMS" on={phones.length > 0} text={SMS_HINT}/>
      </div>
      {hint && <div className="er-small er-error">{hint}</div>}
      {actions ? actions(emails, phones) : (
        <button type="button" className="er-filled" disabled={sending || (emails.length === 0 && phones.length === 0)}
                onClick={() => onSend(emails, phones)}>{label}</button>
      )}
    </div>
  );
};

// ---- The customer's signature (CustomerSignatureStep) ---------------------------------------------

export const SignatureStep = ({working, onSign, onNotNow}: {working: boolean; onSign: (file: File) => void; onNotNow: () => void}) => {
  const canvas = useRef<HTMLCanvasElement>(null);
  const pad = useRef<SignaturePad>();
  const [empty, setEmpty] = useState(true);
  useEffect(() => {
    const el = canvas.current;
    if (!el) return undefined;
    // The pad is as wide as the record (SQ-745), so it is sized from the canvas itself, not the window:
    // the column can change width with the window unchanged (the map opening beside it). Only a change of
    // width re-sizes it, so a phone's address bar showing or hiding never wipes a signature, and what is
    // drawn is kept, stretched across with the pad.
    let width = 0;
    const sized = () => {
      const now = el.offsetWidth;
      if (!now || now === width) return;
      const strokes = pad.current?.toData() ?? [];
      const across = width ? now / width : 1;
      width = now;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      el.width = now * ratio;
      el.height = el.offsetHeight * ratio;
      el.getContext('2d')?.scale(ratio, ratio);
      pad.current?.clear();
      if (strokes.length) {
        pad.current?.fromData(strokes.map(group => ({...group, points: group.points.map(p => ({...p, x: p.x * across}))})));
      }
      setEmpty(pad.current?.isEmpty() ?? true);
    };
    pad.current = new SignaturePad(el, {backgroundColor: 'rgb(255,255,255)'});
    pad.current.addEventListener('endStroke', () => setEmpty(pad.current?.isEmpty() ?? true));
    sized();
    const resizes = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(sized);
    resizes?.observe(el);
    window.addEventListener('resize', sized);
    return () => { resizes?.disconnect(); window.removeEventListener('resize', sized); pad.current?.off(); };
  }, []);
  const sign = () => canvas.current?.toBlob(blob => {
    if (blob) onSign(new File([blob], 'signature.png', {type: 'image/png'}));
  }, 'image/png');
  return (
    <div className="er-sign">
      <div className="er-sign-title">Approve this estimate</div>
      <div className="er-small er-muted">Sign below with your finger. Signing approves the work and the price above.</div>
      <canvas ref={canvas} className="er-sign-pad" aria-label="Signature"/>
      <div className="er-sign-clear">
        <button type="button" className="er-text-button" disabled={empty || working} onClick={() => { pad.current?.clear(); setEmpty(true); }}>Clear</button>
      </div>
      <button type="button" className="er-filled" disabled={empty || working} onClick={sign}>Sign and Confirm</button>
      <div className="er-sign-not-now">
        <button type="button" className="er-text-button er-muted" disabled={working} onClick={onNotNow}>Not now</button>
      </div>
    </div>
  );
};
